import { observer } from "mobx-react";
import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Divider,
  IconButton,
  useTheme,
  Grid2,
  Chip,
} from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { Close as CloseIcon, Input as InputIcon } from "@mui/icons-material";
import AppSearchDropdownMulti from "../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import DatePicker2 from "../../../../../../views/components/common/textFields/DatePicker2";
import dayjs from "dayjs";
import MediaAssetsUpload from "../../../../../../views/components/common/upload/MediaAssetsUpload";
import { useStores } from "../../../../use-stores";
import ImageAPI from "../../../../api/ImageAPI";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import {
  SwageazyInventoryInwardRequestFormData,
  SwageazyInventorySkuLineItem,
} from "../../../../../../models/SwageazyInventory/SwageazyInventory";

interface SwageazyInventoryInwardPopupProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

interface SkuProductGroup {
  productId: number;
  productName: string;
  items: SwageazyInventorySkuLineItem[];
}

const groupSkuItemsByProduct = (
  items: SwageazyInventorySkuLineItem[],
): SkuProductGroup[] => {
  const groups = new Map<number, SkuProductGroup>();
  items.forEach((item) => {
    const existing = groups.get(item.product_id);
    if (existing) {
      existing.items.push(item);
    } else {
      groups.set(item.product_id, {
        productId: item.product_id,
        productName: item.product_name,
        items: [item],
      });
    }
  });
  return Array.from(groups.values());
};

const SwageazyInventoryInwardPopup: React.FC<
  SwageazyInventoryInwardPopupProps
> = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const { swageazyInventoryStore, warehouseStore } = useStores();
  const { showError } = useNotification();

  useEffect(() => {
    if (!open) {
      swageazyInventoryStore.resetInwardRequestFormData();
      if (swageazyInventoryStore.apiTimer) {
        clearTimeout(swageazyInventoryStore.apiTimer);
        swageazyInventoryStore.clearApiTimer();
      }
    } else {
      handleWarehouseSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handlePoSearch = (searchText?: string) => {
    swageazyInventoryStore.clearApiTimer();
    if (searchText && searchText.length > 0) {
      const timer = setTimeout(() => {
        swageazyInventoryStore
          .getPurchaseOrderNumbers(searchText)
          .catch((err) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later.",
            );
          });
      }, 1000);
      swageazyInventoryStore.setApiTimer(timer);
    } else {
      swageazyInventoryStore.setPoSearchResults([]);
      swageazyInventoryStore.clearPurchaseRequestDetail();
      warehouseStore.clearWarehouseRacks();
      swageazyInventoryStore.setInwardRequestFormData({
        po_id: undefined,
        sku_items: [],
        warehouse_id: undefined,
        warehouse_name: undefined,
        current_rack: undefined,
        box_id: undefined,
      });
    }
  };

  const applyWarehouseFromPoOrDefault = () => {
    const detail = swageazyInventoryStore.purchaseRequestDetail;
    const poWarehouseId = detail?.warehouse_id
      ? String(detail.warehouse_id)
      : undefined;

    if (poWarehouseId) {
      const matchedWarehouse = warehouseStore.warehouses?.find(
        (warehouse) => String(warehouse.id) === poWarehouseId,
      );
      swageazyInventoryStore.setInwardRequestFormData({
        warehouse_id: poWarehouseId,
        warehouse_name:
          matchedWarehouse?.identifier ||
          detail?.warehouse_identifier ||
          poWarehouseId,
      });
      handleWarehouseRackSearch(poWarehouseId);
      return;
    }

    if (warehouseStore.warehouses.length === 1) {
      const onlyWarehouse = warehouseStore.warehouses[0];
      swageazyInventoryStore.setInwardRequestFormData({
        warehouse_id: String(onlyWarehouse.id),
        warehouse_name: onlyWarehouse.identifier || String(onlyWarehouse.id),
      });
      handleWarehouseRackSearch(onlyWarehouse.id);
      return;
    }

    handleWarehouseRackSearch(undefined);
  };

  const handlePoSelect = (selected: FilterValue[]) => {
    if (selected?.length > 0) {
      const poId = Number(selected[0]?.id);
      if (!isNaN(poId) && poId > 0) {
        swageazyInventoryStore.setInwardRequestFormData({
          po_id: poId,
          sku_items: [],
        });
        swageazyInventoryStore
          .loadInwardSkuItemsFromPo(poId)
          .then(() => {
            applyWarehouseFromPoOrDefault();
          })
          .catch((err) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Failed to fetch the PO SKUs. Please try again later.",
            );
          });
      }
    } else {
      swageazyInventoryStore.clearPurchaseRequestDetail();
      warehouseStore.clearWarehouseRacks();
      swageazyInventoryStore.setInwardRequestFormData({
        po_id: undefined,
        sku_items: [],
        warehouse_id: undefined,
        warehouse_name: undefined,
        current_rack: undefined,
        box_id: undefined,
      });
      if (warehouseStore.warehouses.length === 1) {
        applyWarehouseFromPoOrDefault();
      }
    }
  };

  const handleWarehouseSearch = () => {
    const assignSingleWarehouseIfNeeded = () => {
      if (
        warehouseStore.warehouses.length === 1 &&
        !swageazyInventoryStore.purchaseRequestDetail?.warehouse_id &&
        !swageazyInventoryStore.inwardRequestFormData?.warehouse_id
      ) {
        swageazyInventoryStore.setInwardRequestFormData({
          warehouse_id: String(warehouseStore.warehouses[0].id),
          warehouse_name:
            warehouseStore.warehouses[0].identifier ||
            String(warehouseStore.warehouses[0].id),
        });
      }

      const poWarehouseId =
        swageazyInventoryStore.purchaseRequestDetail?.warehouse_id;
      if (poWarehouseId) {
        const matchedWarehouse = warehouseStore.warehouses?.find(
          (warehouse) => String(warehouse.id) === String(poWarehouseId),
        );
        swageazyInventoryStore.setInwardRequestFormData({
          warehouse_id: String(poWarehouseId),
          warehouse_name:
            matchedWarehouse?.identifier ||
            swageazyInventoryStore.purchaseRequestDetail?.warehouse_identifier ||
            String(poWarehouseId),
        });
      }
    };

    if (warehouseStore.warehouses.length === 0) {
      warehouseStore
        .searchWarehouse()
        .then(() => {
          assignSingleWarehouseIfNeeded();
        })
        .catch(() => {});
    } else {
      assignSingleWarehouseIfNeeded();
    }
  };

  const isWarehouseLockedFromPo = Boolean(
    swageazyInventoryStore.purchaseRequestDetail?.warehouse_id,
  );

  const selectedWarehouseId =
    swageazyInventoryStore.inwardRequestFormData?.warehouse_id ||
    swageazyInventoryStore.purchaseRequestDetail?.warehouse_id ||
    undefined;

  const selectedWarehouseLabel = (() => {
    if (!selectedWarehouseId) return "";

    const matchedWarehouse = warehouseStore.warehouses?.find(
      (warehouse) => String(warehouse.id) === String(selectedWarehouseId),
    );

    return (
      matchedWarehouse?.identifier ||
      swageazyInventoryStore.inwardRequestFormData?.warehouse_name ||
      swageazyInventoryStore.purchaseRequestDetail?.warehouse_identifier ||
      String(selectedWarehouseId)
    );
  })();

  const handleWarehouseRackSearch = (
    warehouseId: string | number | undefined,
  ) => {
    if (warehouseId) {
      warehouseStore
        .searchWarehouseRack(String(warehouseId), "")
        .then(async () => {
          if (warehouseStore.warehouseRacks?.length === 1) {
            const autoSelectedRackId =
              warehouseStore.warehouseRacks[0]?.id || "";
            swageazyInventoryStore.setInwardRequestFormData({
              current_rack: autoSelectedRackId,
              box_id: undefined,
            });
          } else {
            swageazyInventoryStore.setInwardRequestFormData({
              current_rack: undefined,
              box_id: undefined,
            });
          }
        })
        .catch((err) => {
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          );
          swageazyInventoryStore.setInwardRequestFormData({
            current_rack: undefined,
            box_id: undefined,
          });
        });
    } else {
      warehouseStore.clearWarehouseRacks();
      swageazyInventoryStore.setInwardRequestFormData({
        current_rack: undefined,
        box_id: undefined,
      });
    }
  };

  const skuItems = swageazyInventoryStore.inwardRequestFormData?.sku_items || [];
  const skuGroups = groupSkuItemsByProduct(skuItems);

  const canSubmit = () => {
    const formData = swageazyInventoryStore.inwardRequestFormData;

    if (!formData?.po_id) return false;
    if (!formData?.warehouse_id && !formData?.warehouse_name) return false;
    if (!formData?.current_rack) return false;
    if (!formData?.box_id || formData.box_id.trim() === "") return false;

    const items = formData?.sku_items || [];
    if (items.length === 0) return false;

    // Partial inwards are allowed — at least one SKU must have a received qty.
    const itemsWithQuantity = items.filter(
      (item) => Number(item.quantity) > 0,
    );
    if (itemsWithQuantity.length === 0) return false;

    for (const item of itemsWithQuantity) {
      if (
        Number(item.quantity_rejected) > 0 &&
        (!item.rejection_reason || item.rejection_reason.trim() === "")
      ) {
        return false;
      }
    }

    return true;
  };

  const handleSubmit = () => {
    if (!canSubmit()) return;

    swageazyInventoryStore.setIsLoading(true);
    const images = swageazyInventoryStore.inwardRequestFormData?.images;

    if (images && images.length > 0) {
      const imageAPI = new ImageAPI();
      imageAPI
        .uploadImage(images, "product-images")
        .then((uploadedImageUrls) => {
          swageazyInventoryStore.updateInwardRequestFormData(
            "qc_images",
            uploadedImageUrls,
          );
          onSave();
        })
        .catch((error) => {
          showError(
            "Error Occurred",
            error.response?.data?.message || error.message,
          );
        })
        .finally(() => {
          swageazyInventoryStore.setIsLoading(false);
        });
    } else {
      onSave();
      swageazyInventoryStore.setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            minHeight: { xs: "30vh", sm: "28vh" },
            maxHeight: { xs: "90vh", sm: "85vh", md: "75vh", lg: "70vh" },
            maxWidth: {
              xs: "80vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            width: {
              xs: "80vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          height: "100%",
          minHeight: { xs: "35vh", sm: "32vh" },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <InputIcon
                sx={{
                  color: theme.palette.customColors.blueGradientStart,
                  fontSize: 24,
                  marginTop: 0.2,
                }}
              />
              <CustomTypography variant="heading3">
                Swageazy Inventory Inward
              </CustomTypography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 3,
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Grid2 container spacing={2} alignItems="stretch">
              <Grid2
                size={{ xs: 12, sm: 6, md: 6 }}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  PO Number{" "}
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <AppSearchDropdownMulti
                  title="PO Number"
                  isMultiSelect={false}
                  list={swageazyInventoryStore.poSearchResults || []}
                  value={
                    swageazyInventoryStore.inwardRequestFormData?.po_id
                      ? [
                          {
                            id: String(
                              swageazyInventoryStore.inwardRequestFormData
                                ?.po_id,
                            ),
                            value: String(
                              swageazyInventoryStore.inwardRequestFormData
                                ?.po_id,
                            ),
                          },
                        ]
                      : []
                  }
                  handleSelect={handlePoSelect}
                  handleSearch={handlePoSearch}
                  local={false}
                  minWidth="100%"
                  borderRadius="8px"
                />
              </Grid2>

              <Grid2
                size={{ xs: 12, sm: 6, md: 6 }}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  Vendor Details
                </CustomTypography>
                <Box
                  sx={{
                    minHeight: 40,
                    border: `1px solid ${theme.palette.customColors.greyBorder}`,
                    borderRadius: "8px",
                    px: 1.5,
                    py: 1.25,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    backgroundColor:
                      theme.palette.customColors?.lightGreyColor || "#F8FAFC",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: theme.palette.text.primary, minWidth: 88 }}
                    >
                      Vendor ID:
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {swageazyInventoryStore.purchaseRequestDetail
                        ?.vendor_id ?? "-"}
                    </CustomTypography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: theme.palette.text.primary, minWidth: 88 }}
                    >
                      Vendor Name:
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {swageazyInventoryStore.purchaseRequestDetail
                        ?.vendor_name || "-"}
                    </CustomTypography>
                  </Box>
                </Box>
              </Grid2>
            </Grid2>

            {swageazyInventoryStore.inwardRequestFormData?.po_id && (
              <Box sx={{ mt: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 2,
                  }}
                >
                  <CustomTypography
                    variant="heading6"
                    style={{
                      color: theme.palette.customColors.blackBorder,
                      fontWeight: 600,
                    }}
                  >
                    PO SKUs{" "}
                    <span
                      style={{
                        color: theme.palette.error.main,
                        marginLeft: 4,
                      }}
                    >
                      *
                    </span>
                  </CustomTypography>
                  <CustomTypography
                    variant="body3"
                    style={{ color: theme.palette.text.secondary }}
                  >
                    Enter the received quantity only for the SKUs that arrived.
                  </CustomTypography>
                </Box>

                {skuGroups.length === 0 ? (
                  <Box
                    sx={{
                      p: 3,
                      textAlign: "center",
                      color: theme.palette.customColors.greyTextColor,
                      border: `1px dashed ${theme.palette.customColors.greyBorder}`,
                      borderRadius: "8px",
                      backgroundColor:
                        theme.palette.customColors.lightGreyColor,
                    }}
                  >
                    <CustomTypography variant="body1">
                      No SKUs found for this PO
                    </CustomTypography>
                  </Box>
                ) : (
                  skuGroups.map((group) => (
                    <Box
                      key={group.productId}
                      sx={{
                        marginBottom: 2,
                        padding: { xs: 1.5, sm: 2 },
                        border: `1px solid ${theme.palette.customColors.greyBorder}`,
                        borderRadius: "8px",
                        backgroundColor:
                          theme.palette.customColors.lightGreyColor,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                          mb: 1.5,
                        }}
                      >
                        <CustomTypography variant="heading6" fontWeight={600}>
                          {group.productName}
                        </CustomTypography>
                        <Chip
                          size="small"
                          variant="outlined"
                          color="primary"
                          label={`${group.items.length} ${
                            group.items.length === 1 ? "SKU" : "SKUs"
                          }`}
                        />
                      </Box>

                      {group.items.map((item) => (
                        <Box
                          key={`${item.product_id}-${item.sku}`}
                          sx={{
                            mb: 1.5,
                            p: { xs: 1.5, sm: 2 },
                            border: `1px solid ${theme.palette.customColors.greyBorder}`,
                            borderRadius: "8px",
                            backgroundColor: theme.palette.customColors.white,
                          }}
                        >
                          <Grid2
                            container
                            spacing={2}
                            sx={{ width: "100%", alignItems: "flex-start" }}
                          >
                            <Grid2
                              size={{ xs: 12, sm: 6, md: 3 }}
                              sx={{ minWidth: 0 }}
                            >
                              <CustomTypography
                                variant="body1"
                                fontWeight={600}
                              >
                                {item.title || item.sku}
                              </CustomTypography>
                              <CustomTypography
                                variant="body3"
                                style={{
                                  color: theme.palette.text.secondary,
                                  wordBreak: "break-all",
                                }}
                              >
                                SKU: {item.sku}
                              </CustomTypography>
                              {item.expected_quantity !== undefined && (
                                <CustomTypography
                                  variant="body3"
                                  style={{
                                    color: theme.palette.text.secondary,
                                  }}
                                >
                                  PR Qty: {item.expected_quantity}
                                </CustomTypography>
                              )}
                            </Grid2>
                            <Grid2
                              size={{ xs: 12, sm: 6, md: 3 }}
                              sx={{ minWidth: 0 }}
                            >
                              <CustomTextField
                                width="100%"
                                label="Qty Received"
                                placeholder="Qty Received"
                                value={item.quantity ?? ""}
                                onChange={(e) =>
                                  swageazyInventoryStore.updateInwardSkuItem(
                                    item.sku,
                                    item.product_id,
                                    {
                                      quantity: e.target.value
                                        ? Number(e.target.value)
                                        : undefined,
                                    },
                                  )
                                }
                                type="number"
                                allowNegativeValue={false}
                                allowDecimalValue={false}
                              />
                            </Grid2>
                            <Grid2
                              size={{ xs: 12, sm: 6, md: 3 }}
                              sx={{ minWidth: 0 }}
                            >
                              <CustomTextField
                                width="100%"
                                label="Qty Rejected"
                                placeholder="Qty Rejected"
                                value={item.quantity_rejected ?? ""}
                                onChange={(e) =>
                                  swageazyInventoryStore.updateInwardSkuItem(
                                    item.sku,
                                    item.product_id,
                                    {
                                      quantity_rejected: e.target.value
                                        ? Number(e.target.value)
                                        : undefined,
                                    },
                                  )
                                }
                                allowNegativeValue={false}
                                allowDecimalValue={false}
                                type="number"
                              />
                            </Grid2>
                            <Grid2
                              size={{ xs: 12, sm: 6, md: 3 }}
                              sx={{ minWidth: 0 }}
                            >
                              <DatePicker2
                                type="date"
                                label="Expiry date (optional)"
                                value={
                                  item.expiry_date
                                    ? dayjs(item.expiry_date)
                                    : null
                                }
                                onChange={(newValue) =>
                                  swageazyInventoryStore.updateInwardSkuItem(
                                    item.sku,
                                    item.product_id,
                                    {
                                      expiry_date: newValue
                                        ? newValue.format("YYYY-MM-DD")
                                        : undefined,
                                    },
                                  )
                                }
                                borderRadius="8px"
                                borderColor="#E0E0E0"
                                backgroundColor={
                                  theme.palette.customColors
                                    .grayBackGroundColor
                                }
                                zIndex={4000}
                              />
                            </Grid2>
                            {Number(item.quantity_rejected) > 0 && (
                              <Grid2 size={{ xs: 12 }} sx={{ minWidth: 0 }}>
                                <CustomTextField
                                  width="100%"
                                  label="Rejection Reason"
                                  placeholder="Rejection Reason"
                                  value={item.rejection_reason ?? ""}
                                  type="text"
                                  onChange={(e) =>
                                    swageazyInventoryStore.updateInwardSkuItem(
                                      item.sku,
                                      item.product_id,
                                      { rejection_reason: e.target.value },
                                    )
                                  }
                                />
                              </Grid2>
                            )}
                          </Grid2>
                        </Box>
                      ))}
                    </Box>
                  ))
                )}
              </Box>
            )}
          </Box>

          <Divider sx={{ marginBottom: 1 }} />

          <Box
            sx={{
              mb: 1,
              p: 2,
              backgroundColor: theme.palette.customColors.lightGreyColor,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.customColors.greyBorder}`,
            }}
          >
            <CustomTypography
              variant="body2"
              style={{
                color: theme.palette.text.secondary,
                lineHeight: "1.6",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
              }}
            >
              <span
                style={{ fontWeight: 600, color: theme.palette.text.primary }}
              >
                Note:
              </span>
              <span>
                Choose Warehouse → Rack. After selecting a Rack, enter the Box
                ID or click Generate to create a system-generated Box ID.
              </span>
            </CustomTypography>
          </Box>

          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Warehouse{" "}
                <span
                  style={{
                    color: theme.palette.error.main,
                    marginLeft: 4,
                  }}
                >
                  *
                </span>
              </CustomTypography>
              <AppSearchDropdownMulti
                isMultiSelect={false}
                title="Select Warehouse"
                local
                disabled={isWarehouseLockedFromPo}
                backgroundColor={
                  theme.palette.customColors.grayBackGroundColor
                }
                borderColor={theme.palette.customColors.greyBodyBackground}
                minWidth="13.938rem"
                list={
                  warehouseStore.warehouses?.map((item) => {
                    return {
                      id: String(item?.id),
                      value: String(item?.identifier || item?.id),
                    };
                  }) || []
                }
                value={
                  selectedWarehouseId
                    ? [
                        {
                          id: String(selectedWarehouseId),
                          value: selectedWarehouseLabel,
                        },
                      ]
                    : []
                }
                handleSelect={(selectedItem: FilterValue[]) => {
                  if (isWarehouseLockedFromPo) return;

                  if (selectedItem && selectedItem.length > 0) {
                    const warehouseUpdate: SwageazyInventoryInwardRequestFormData =
                      {};

                    warehouseUpdate.warehouse_id = selectedItem[0].id;
                    warehouseUpdate.warehouse_name = selectedItem[0].value;

                    swageazyInventoryStore.setInwardRequestFormData(
                      warehouseUpdate,
                    );

                    handleWarehouseRackSearch(selectedItem[0].id);
                  } else {
                    swageazyInventoryStore.setInwardRequestFormData({
                      warehouse_id: undefined,
                      warehouse_name: undefined,
                    });

                    handleWarehouseRackSearch(undefined);
                  }
                }}
              />
              {isWarehouseLockedFromPo ? (
                <CustomTypography
                  variant="body3"
                  sx={{
                    display: "block",
                    mt: 0.75,
                    color: theme.palette.text.secondary,
                  }}
                >
                  Warehouse is set from the selected PO and cannot be changed.
                </CustomTypography>
              ) : null}
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Rack{" "}
                <span
                  style={{
                    color: theme.palette.error.main,
                    marginLeft: 4,
                  }}
                >
                  *
                </span>
              </CustomTypography>
              <AppSearchDropdownMulti
                title="Select Rack"
                isMultiSelect={false}
                list={
                  warehouseStore.warehouseRacks?.map((item) => {
                    return {
                      id: String(item?.id),
                      value: String(item?.id),
                    };
                  }) || []
                }
                value={
                  swageazyInventoryStore.inwardRequestFormData?.current_rack
                    ? [
                        {
                          id: swageazyInventoryStore.inwardRequestFormData
                            ?.current_rack,
                          value:
                            swageazyInventoryStore.inwardRequestFormData
                              ?.current_rack,
                        },
                      ]
                    : []
                }
                handleSelect={async (selectedItem: FilterValue[]) => {
                  if (selectedItem?.length > 0) {
                    const selectedRackId = selectedItem[0]?.id || "";
                    swageazyInventoryStore.setInwardRequestFormData({
                      current_rack: selectedRackId,
                      box_id: undefined,
                    });
                  } else {
                    swageazyInventoryStore.setInwardRequestFormData({
                      current_rack: undefined,
                      box_id: undefined,
                    });
                  }
                }}
                local
                minWidth="100%"
                borderRadius="8px"
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  justifyContent: "space-between",
                  marginBottom: 1,
                }}
              >
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600 }}
                >
                  Box ID{" "}
                  <span
                    style={{
                      color: theme.palette.error.main,
                      marginLeft: 4,
                    }}
                  >
                    *
                  </span>
                </CustomTypography>

                <IconButton
                  disabled={
                    !!swageazyInventoryStore.inwardRequestFormData?.box_id
                  }
                  sx={{
                    color: swageazyInventoryStore.inwardRequestFormData?.box_id
                      ? theme.palette.customColors.greyTextColor
                      : theme.palette.customColors.pinkDark,
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                    "&.Mui-disabled": {
                      color: theme.palette.customColors.greyTextColor,
                    },
                  }}
                  size="small"
                  onClick={() => {
                    if (
                      !swageazyInventoryStore.inwardRequestFormData
                        ?.warehouse_id
                    ) {
                      showError(
                        "Validation Error",
                        "Please select a Warehouse first before generating Box ID.",
                      );
                      return;
                    }
                    if (
                      !swageazyInventoryStore.inwardRequestFormData
                        ?.current_rack
                    ) {
                      showError(
                        "Validation Error",
                        "Please select a Rack ID first before generating Box ID.",
                      );
                      return;
                    }
                    swageazyInventoryStore
                      .generateBoxId(
                        swageazyInventoryStore.inwardRequestFormData!
                          .current_rack!,
                        swageazyInventoryStore.inwardRequestFormData!
                          .warehouse_id!,
                      )
                      .then((boxId) => {
                        if (!boxId) {
                          showError(
                            "Error",
                            "Failed to generate box ID. Please try again.",
                          );
                          return;
                        }
                        swageazyInventoryStore.setInwardRequestFormData({
                          box_id: boxId,
                        });
                      })
                      .catch(() => {
                        showError(
                          "Error",
                          "Failed to generate box ID. Please try again.",
                        );
                      });
                  }}
                >
                  <CustomTypography
                    variant="heading7"
                    color={
                      swageazyInventoryStore.inwardRequestFormData?.box_id
                        ? theme.palette.customColors.greyTextColor
                        : theme.palette.customColors.pinkDark
                    }
                  >
                    {swageazyInventoryStore.inwardRequestFormData?.box_id
                      ? "Generated"
                      : "Generate"}
                  </CustomTypography>
                </IconButton>
              </Box>
              <CustomTextField
                width="100%"
                placeholder="Enter Box ID or click Generate"
                value={
                  swageazyInventoryStore.inwardRequestFormData?.box_id || ""
                }
                onChange={(e) => {
                  swageazyInventoryStore.setInwardRequestFormData({
                    box_id: e.target.value,
                  });
                }}
              />
            </Grid2>
          </Grid2>

          <Box sx={{ mb: 3 }}>
            <CustomTypography
              variant="heading6"
              style={{ fontWeight: 600, marginBottom: 16 }}
            >
              Images (Optional)
            </CustomTypography>
            <Box sx={{ width: "100%", maxWidth: "100%" }}>
              <MediaAssetsUpload
                key={`inward-images-${
                  swageazyInventoryStore.inwardRequestFormData?.po_id || "new"
                }`}
                existingImageList={[]}
                newImageList={
                  (swageazyInventoryStore.inwardRequestFormData?.images ||
                    []) as File[]
                }
                multiple={true}
                uploadText="Upload Image"
                fileInfo="PNG, JPG up to 10MB each"
                removeImage={(image) => {
                  const fileIndex =
                    typeof image.id === "string" && image.id.startsWith("file-")
                      ? parseInt(image.id.replace("file-", ""), 10)
                      : Number(image.id);

                  if (fileIndex !== undefined && fileIndex >= 0) {
                    const updatedFiles =
                      swageazyInventoryStore.inwardRequestFormData?.images?.filter(
                        (_, index) => index !== fileIndex,
                      ) || [];
                    swageazyInventoryStore.setInwardRequestFormData({
                      images: updatedFiles,
                    });
                  }
                }}
                addImagesToBeUploaded={(files) => {
                  swageazyInventoryStore.setInwardRequestFormData({
                    images: [
                      ...(swageazyInventoryStore.inwardRequestFormData
                        ?.images || []),
                      ...files,
                    ],
                  });
                }}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "10px 16px 10px", sm: "16px 20px 20px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <AppButton
            onClick={onClose}
            variant="grayscale"
            label="Cancel"
            customStyles={{
              borderColor: theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.greyTextColor,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-1px)",
              },
            }}
          />

          <AppButton
            onClick={handleSubmit}
            variant="primary"
            label="Submit"
            disabled={!canSubmit()}
            customStyles={{
              backgroundColor: canSubmit()
                ? theme.palette.customColors.EmeraldGreen
                : theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.white,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: canSubmit()
                  ? theme.palette.customColors.greenDark
                  : theme.palette.customColors.greyDarkborder,
                transform: canSubmit() ? "translateY(-1px)" : "none",
                boxShadow: canSubmit()
                  ? `0 4px 12px ${theme.palette.customColors.EmeraldGreen}30`
                  : "none",
              },
              "&:disabled": {
                backgroundColor: theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.white,
                cursor: "not-allowed",
              },
            }}
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
};
export default observer(SwageazyInventoryInwardPopup);
